"use client";

import { ReactNode, useState } from "react";
import {
  PiCaretDown,
  PiChartBarFill,
  PiFileVideoFill,
  PiInstagramLogo,
  PiMicrophoneFill,
  PiNewspaperClippingFill,
  PiNotebookFill,
  PiNotepadFill,
  PiQuestionFill,
} from "react-icons/pi";

import { PAGES } from "@/configs/pages.config";
import { CHATGPT_GUIDE_PATH, CHATGPT_PLUGIN_LIVE, CHATGPT_TOOL_PATH } from "@/lib/chatgpt-plugin";
import { getChatGptCopy } from "@/lib/i18n/chatgpt";
import { SupportedLocale, withLocalePath } from "@/lib/i18n/locales";
import { getNavigationCopy } from "@/lib/i18n/navigation";
import { isLocalizedSeoPath } from "@/lib/i18n/seo";
import { cn } from "@/lib/utils";

import Link, { linkVariants } from "../link/link";

type DefaultLink = {
  href: string;
  title: string;
};

type DropdownLink = {
  title: string;
  align?: "end";
  dropdown: (DefaultLink & {
    description: string;
    icon: ReactNode;
  })[];
};

type NavigationLink = DefaultLink | DropdownLink;

export function getNavigationLinks(locale: SupportedLocale): NavigationLink[] {
  const copy = getNavigationCopy(locale);
  const localizeHref = (href: string) => {
    if (!href.startsWith("/")) {
      return href;
    }

    return isLocalizedSeoPath(href) ? withLocalePath(locale, href) : href;
  };

  return [
    {
      href: localizeHref(PAGES.TOOLS.PATH),
      title: copy.links.tools,
    },
    {
      title: copy.links.useCases,
      dropdown: [
        ...(CHATGPT_PLUGIN_LIVE
          ? [
              {
                href: localizeHref(CHATGPT_TOOL_PATH),
                title: getChatGptCopy(locale).name,
                description: getChatGptCopy(locale).description,
                icon: <PiNotebookFill className="w-6 h-6 lg:w-8 lg:h-8" />,
              },
            ]
          : []),
        {
          href: localizeHref(PAGES.PODCAST_TRANSCRIPTION.PATH),
          title: copy.links.podcast.title,
          icon: <PiMicrophoneFill className="w-6 h-6 lg:w-8 lg:h-8" />,
          description: copy.links.podcast.description,
        },
        {
          href: localizeHref(PAGES.VIDEO_TO_TEXT.PATH),
          title: copy.links.videoToText.title,
          icon: <PiFileVideoFill className="w-6 h-6 lg:w-8 lg:h-8" />,
          description: copy.links.videoToText.description,
        },
        {
          href: localizeHref(PAGES.INSTAGRAM_TO_TEXT.PATH),
          title: copy.links.instagramToText.title,
          icon: <PiInstagramLogo className="w-6 h-6 lg:w-8 lg:h-8" />,
          description: copy.links.instagramToText.description,
        },
        {
          href: localizeHref(PAGES.TIKTOK_TO_TRANSCRIPT.PATH),
          title: copy.links.tiktokToTranscript.title,
          icon: <PiFileVideoFill className="w-6 h-6 lg:w-8 lg:h-8" />,
          description: copy.links.tiktokToTranscript.description,
        },
        {
          href: localizeHref(PAGES.INSTAGRAM_ANALYTICS.PATH),
          title: copy.links.instagramAnalytics.title,
          icon: <PiChartBarFill className="w-6 h-6 lg:w-8 lg:h-8" />,
          description: copy.links.instagramAnalytics.description,
        },
        {
          href: localizeHref(PAGES.REEL_TO_POST_CONVERTER.PATH),
          title: copy.links.reelToPost.title,
          icon: <PiInstagramLogo className="w-6 h-6 lg:w-8 lg:h-8" />,
          description: copy.links.reelToPost.description,
        },
        {
          href: localizeHref(PAGES.ITINERARY_EXTRACTOR.PATH),
          title: copy.links.itineraryExtractor.title,
          icon: <PiInstagramLogo className="w-6 h-6 lg:w-8 lg:h-8" />,
          description: copy.links.itineraryExtractor.description,
        },
        {
          href: localizeHref(PAGES.COOKING_RECIPES.PATH),
          title: copy.links.cooking.title,
          icon: <PiNotepadFill className="w-6 h-6 lg:w-8 lg:h-8" />,
          description: copy.links.cooking.description,
        },
        {
          href: localizeHref(PAGES.VIDEO_BLOG.PATH),
          title: copy.links.videoBlog.title,
          icon: <PiNewspaperClippingFill className="w-6 h-6 lg:w-8 lg:h-8" />,
          description: copy.links.videoBlog.description,
        },
      ],
    },
    {
      href: localizeHref(PAGES.PRICING.PATH),
      title: copy.links.pricing,
    },
    {
      href: localizeHref(PAGES.FEATURES.PATH),
      title: copy.links.features,
    },
    {
      title: copy.links.resources,
      align: "end",
      dropdown: [
        ...(CHATGPT_PLUGIN_LIVE
          ? [
              {
                href: localizeHref(CHATGPT_GUIDE_PATH),
                title: getChatGptCopy(locale).guide,
                description: getChatGptCopy(locale).guideDescription,
                icon: <PiNotebookFill className="w-6 h-6 lg:w-8 lg:h-8" />,
              },
            ]
          : []),
        {
          href: localizeHref(PAGES.SUPPORT.PATH),
          title: copy.links.support.title,
          icon: <PiQuestionFill className="w-6 h-6 lg:w-8 lg:h-8" />,
          description: copy.links.support.description,
        },
        {
          href: localizeHref(PAGES.BLOG.PATH),
          title: copy.links.blog.title,
          icon: <PiNotebookFill className="w-6 h-6 lg:w-8 lg:h-8" />,
          description: copy.links.blog.description,
        },
      ],
    },
  ];
}

export default function NavigationLink(props: NavigationLink) {
  const [dropdown, setDropdown] = useState(false);

  if ("href" in props) {
    return (
      <Link href={props.href} color="white-primary" className="h-auto lg:h-[var(--navbar-height)] text-base">
        {props.title}
      </Link>
    );
  }

  return (
    <div
      className="h-auto lg:h-[var(--navbar-height)] group relative outline-none focus-visible:outline-primary"
      tabIndex={0}
      onMouseEnter={() => setDropdown(true)}
      onMouseLeave={() => setDropdown(false)}
      onFocus={() => setDropdown(true)}
      onBlur={() => setDropdown(false)}
    >
      <div className={cn(linkVariants({ color: "white-primary" }), "h-auto lg:h-full text-base cursor-default")}>
        <span>{props.title}</span>
        <PiCaretDown className="w-4 h-4 group-hover:rotate-180 transition-transform duration-500" />
      </div>
      <div
        className={cn(
          "hidden invisible scale-90 opacity-0 absolute top-[calc(var(--navbar-height)-0.25rem)] w-[min(40rem,calc(100vw-2rem))]",
          props.align === "end" ? "right-0 origin-top-right" : "left-0 origin-top-left",
          "bg-white border border-gray-600 shadow-md shadow-gray-400 rounded-2xl p-3 lg:grid grid-cols-2 gap-4 text-sm transition-all duration-300",
          dropdown && "h-fit visible opacity-100 scale-100"
        )}
      >
        <div className={cn("absolute -top-2 h-4 w-4 rotate-45 bg-white", props.align === "end" ? "right-10" : "left-10")} />
        {props.dropdown.map(link => (
          <Link
            key={link.title}
            href={link.href}
            color="plain"
            className="flex min-w-0 gap-2 items-center text-wrap rounded-lg p-2 hover:bg-gray-200 border border-transparent hover:border-gray-600"
          >
            <div className="shrink-0 text-primary">{link.icon}</div>
            <div className="min-w-0 break-words">
              {link.title}
              <p className="text-xs text-gray-600">{link.description}</p>
            </div>
          </Link>
        ))}
      </div>
      <div
        className={cn(
          "lg:hidden max-h-0 invisible flex flex-col items-start gap-y-2 transition-all duration-300 ease-out overflow-hidden",
          dropdown && "max-h-96 visible pt-4"
        )}
      >
        {props.dropdown.map(link => (
          <Link key={link.title} href={link.href} color="white-primary">
            {link.icon}
            {link.title}
          </Link>
        ))}
      </div>
    </div>
  );
}
